<template>
  <Modal
    title="Payment Method"
    :saving="saving"
    @hide-modal="$emit('hide-modal')"
    @submit="handleSubmit"
  >
    <div v-if="paymentMethods.length && !loadingMethods">
      <CustomerPaymentMethods
        v-if="customer.payment_method"
        :customer="customer"
        @payment-method-removed="handleRemove"
      />
      <div v-else>
        <div class="d-flex align-items-center mb-2">
          <b class="mr-3">Pay With:</b>
          <ButtonToggle
            v-model="methodType"
            class="toggle-button"
            :options="methodTypes"
          />
        </div>

        <GPICardForm
          v-if="methodType === 'card' && gpiEnabled"
          ref="form"
          auto-save-card
          :customer="customer"
        />
        <StaxCardForm
          v-if="methodType === 'card' && staxEnabled"
          ref="form"
          auto-save-card
          :customer="customer"
        />
        <ACHForm
          v-if="methodType === 'ach' && gpiAchEnabled"
          ref="achForm"
          :form="achFields"
          auto-save-card
        />
        <StaxAchForm
          v-if="methodType === 'ach' && staxAchEnabled"
          ref="achForm"
          :form="achFields"
          :customer="customer"
          auto-save-card
        />
      </div>
    </div>
    <div v-else>
      <span
        v-if="paymentMethods.length === 0 && !loadingMethods"
        class="no-results"
        >No Payment Methods Available</span
      >
      <span v-if="loadingMethods" class="no-results"
        ><Spinner class="mt-3" message="Loading Payment Methods..."
      /></span>
    </div>
    <template #submit>
      <div v-if="customer.payment_method"></div>
    </template>
  </Modal>
</template>

<script>
import { mapActions, mapState } from "vuex";
import { EventBus } from "@/lib/event_bus.js";

import ButtonToggle from "@/components/ButtonToggle.vue";
import Modal from "@/components/Modal.vue";
import Spinner from "@/components/Spinner.vue";
import ACHForm from "@/AcceptPayment/ACHForm.vue";
import CustomerPaymentMethods from "@/AcceptPayment/CustomerPaymentMethods.vue";
import GPICardForm from "@/AcceptPayment/GPICardForm.vue";
import StaxAchForm from "@/AcceptPayment/StaxAchForm.vue";
import StaxCardForm from "@/AcceptPayment/StaxCardForm.vue";
import usePaymentMethods from "@/AcceptPayment/usePaymentMethods";
import useVuelidate from "@vuelidate/core";

export default {
  components: {
    Modal,
    GPICardForm,
    Spinner,
    StaxCardForm,
    ACHForm,
    StaxAchForm,
    CustomerPaymentMethods,
    ButtonToggle
  },
  props: {
    customer: {
      type: Object,
      default: null
    }
  },
  emits: ["hide-modal", "save-success"],
  setup() {
    const {
      fetchPaymentMethods,
      fetchCustomer: fetchCustomerForPaymentMethods,
      paymentMethodsResponse
    } = usePaymentMethods();
    return {
      fetchCustomerForPaymentMethods,
      fetchPaymentMethods,
      paymentMethodsResponse,
      v$: useVuelidate()
    };
  },
  data() {
    return {
      loadingMethods: true,
      deleting: false,
      saving: false,
      methodType: "card",
      achFields: {
        transaction_type: "ach",
        ach_first_name: this.customer.first_name,
        ach_last_name: this.customer.last_name,
        ach_account_type: "checking",
        ach_account_number: "",
        ach_routing_number: "",
        ach_save_payment_method: true
      }
    };
  },
  computed: {
    ...mapState({
      paymentMethods() {
        return this.paymentMethodsResponse.map((method) => {
          if (method.key === "payment_method") {
            return {
              ...method,
              value: method.key,
              disabled: !this.customer.payment_method
            };
          }
          return {
            ...method,
            value: method.key
          };
        });
      },
      gpiEnabled: ({ paymentMethods }) => {
        return paymentMethods.some((method) => method.key === "gpi_card");
      },
      staxEnabled: ({ paymentMethods }) => {
        return paymentMethods.some((method) => method.key === "stax_card");
      },
      ccEnabled: ({ paymentMethods }) => {
        return paymentMethods.some((method) =>
          ["gpi_card", "stax_card"].includes(method.key)
        );
      },
      achEnabled: ({ paymentMethods }) => {
        return paymentMethods.some((method) =>
          ["gpi_ach", "stax_ach"].includes(method.key)
        );
      },
      gpiAchEnabled: ({ paymentMethods }) => {
        return paymentMethods.some((method) =>
          ["gpi_ach"].includes(method.key)
        );
      },
      staxAchEnabled: ({ paymentMethods }) => {
        return paymentMethods.some((method) =>
          ["stax_ach"].includes(method.key)
        );
      },
      methodTypes: function () {
        return [
          {
            label: "Credit Card",
            value: "card",
            disabled: !this.ccEnabled
          },
          { label: "ACH", value: "ach", disabled: !this.achEnabled }
        ];
      }
    })
  },
  async mounted() {
    this.refreshData();
  },
  methods: {
    ...mapActions(["confirmDelete", "createPaymentMethod"]),
    async refreshData() {
      this.loadingMethods = true;
      try {
        await this.fetchCustomerForPaymentMethods(this.customer.id);
        await this.fetchPaymentMethods();
      } catch (e) {
        // Do nothing
      }
      this.loadingMethods = false;
    },
    handleRemove() {
      window.notify.success("Payment method removed successfully!");
      this.$emit("save-success");
      this.$emit("hide-modal");

      // for any other components
      EventBus.$emit("payment-method-removed");
    },
    handleSubmit() {
      this.saving = true;
      let getPayload = this.submitACH;
      if (this.methodType === "card") {
        if (this.staxEnabled) {
          getPayload = this.submitStax;
        }
        if (this.gpiEnabled) {
          getPayload = this.submitGPI;
        }
      }
      // Get payload from processor form and submit create request
      getPayload()
        .then((params) => {
          return this.createPaymentMethod({
            url: this.customer.payment_method_create_url,
            customer_id: this.customer.id,
            ...params
          }).then(() => {
            window.notify.success("Payment method added successfully!");
            this.$emit("save-success");
            this.$emit("hide-modal");

            EventBus.$emit("payment-method-updated");
          });
        })
        .finally(() => {
          this.saving = false;
        });
    },
    submitStax() {
      return new Promise((resolve, reject) => {
        return this.$refs.form
          .getData()
          .then((cardData) => {
            resolve({
              ...cardData,
              transaction_type: "card"
            });
          })
          .catch((err) => reject(err));
      });
    },
    submitGPI() {
      // Get card data from GPI form
      return new Promise((resolve, reject) => {
        this.$refs.form
          .getData()
          .then((cardData) => {
            resolve({
              ...cardData,
              transaction_type: "card"
            });
          })
          .catch(() => {
            reject();
          });
      });
    },
    async submitACH() {
      // if there is a customer handler for the form, use it
      const getData = this.$refs.achForm ? this.$refs.achForm.getData : null;
      if (getData) {
        return getData();
      }

      // Otherwise, use the default handler
      window.notify.close();
      const validated = await v$.$validate();
      return new Promise((resolve, reject) => {
        if (validated) {
          this.saving = true;
          resolve(this.achFields);
        } else {
          reject();
        }
      });
    }
  }
};
</script>

<style lang="scss" scoped>
.toggle-button {
  width: 200px;
}
</style>
